import { Component, computed, DestroyRef, ElementRef, inject, OnInit, OnDestroy, Pipe, PipeTransform, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';

import { LivefeedService } from '../services/livefeed.service';
import { AmazonImagePipe } from '../shared/amazon-image.pipe';
import { CURRENCY_MAP, DOMAIN_KEYS, CATEGORIES } from '../../../shared/constants';
import { DomainKey } from '../../../shared/types/product';
import { LivefeedEvent } from '../../../shared/types/livefeed-event';

@Pipe({ name: 'timeAgo', standalone: true })
export class TimeAgoPipe implements PipeTransform {
  transform(value: string | Date): string {
    const date = typeof value === 'string' ? new Date(value) : value;
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
}

@Component({
  selector: 'app-livefeed',
  templateUrl: './livefeed.component.html',
  styleUrls: ['./livefeed.component.scss'],
  standalone: true,
  imports: [
    CurrencyPipe,
    DecimalPipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    TimeAgoPipe,
    AmazonImagePipe,
  ],
})
export class LivefeedComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef<HTMLElement>;
  currencyMap = CURRENCY_MAP;
  domain: DomainKey = '1';

  // Filter state — three user-facing types (service remaps DB "priced" → "new")
  private static readonly ALL_TYPES = new Set(['new', 'price_change', 'reactivated']);
  readonly filterTypes = signal(new Set(['new', 'price_change', 'reactivated']));
  readonly selectedCategory = signal<string>('');
  readonly minSavings = signal(0);
  readonly searchText = signal('');
  readonly filtersExpanded = signal(false);

  readonly FILTER_OPTIONS: { key: string; label: string }[] = [
    { key: 'new', label: 'New' },
    { key: 'price_change', label: 'Price Change' },
    { key: 'reactivated', label: 'Reactivated' },
  ];

  categoryOptions: { name: string; rootcat: number }[] = [];

  readonly filteredEvents = computed(() => {
    const events = this.livefeedService.events();
    const types = this.filterTypes();
    const cat = this.selectedCategory();
    const minSav = this.minSavings();
    const search = this.searchText().toLowerCase().trim();

    return events.filter(e => {
      if (!types.has(e.event_type)) return false;
      if (cat && e.category !== cat) return false;
      if (minSav > 0 && (e.displaysavingspercent == null || e.displaysavingspercent < minSav)) return false;
      if (search && (!e.title || !e.title.toLowerCase().includes(search))) return false;
      return true;
    });
  });

  readonly hasActiveFilters = computed(() => {
    return this.filterTypes().size < LivefeedComponent.ALL_TYPES.size
      || this.selectedCategory() !== ''
      || this.minSavings() > 0
      || this.searchText().trim() !== '';
  });

  private static readonly SCROLL_THRESHOLD = 50;

  constructor(
    readonly livefeedService: LivefeedService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const domainParam = params['domain'];
        if (domainParam && DOMAIN_KEYS.indexOf(domainParam) >= 0) {
          this.domain = domainParam;
        }
        this.categoryOptions = Object.entries(CATEGORIES[this.domain])
          .map(([name, rootcat]) => ({ name, rootcat }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.livefeedService.switchDomain(this.domain);
      });
  }

  ngOnDestroy(): void {
    this.livefeedService.disconnect();
  }

  toggleFilterType(type: string): void {
    this.filterTypes.update(s => {
      const next = new Set(s);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  clearFilters(): void {
    this.filterTypes.set(new Set(LivefeedComponent.ALL_TYPES));
    this.selectedCategory.set('');
    this.minSavings.set(0);
    this.searchText.set('');
  }

  onScroll(): void {
    const el = this.scrollContainer.nativeElement;
    const scrolledDown = el.scrollTop > LivefeedComponent.SCROLL_THRESHOLD;
    this.livefeedService.buffering.set(scrolledDown);
    if (!scrolledDown) {
      this.livefeedService.flushPending();
    }
  }

  showNewEvents(): void {
    this.livefeedService.flushPending();
    this.scrollContainer.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  eventTypeLabel(type: string): string {
    switch (type) {
      case 'new': return 'New';
      case 'price_change': return 'Price Change';
      case 'reactivated': return 'Reactivated';
      default: return type;
    }
  }

  trackByEventId(_index: number, event: LivefeedEvent): number {
    return event.id;
  }
}
